import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';

// Landing page
import LandingPage from './pages/landing/LandingPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';

// Superadmin components
import Layout from './components/Layout';
import SetupWizard from './components/SetupWizard';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import Analytics from './pages/Analytics';
import SystemHealth from './pages/SystemHealth';
import Settings from './pages/Settings';
import InviteLinks from './pages/InviteLinks';

// Tenant Admin components
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfigProvider } from './context/ConfigContext';
import { UploadProvider } from './context/UploadContext';
import AdminLayout from './components/admin/Layout';
import AdminLogin from './pages/admin/Login';
import AdminSignup from './pages/admin/Signup';
import AdminForgotPassword from './pages/admin/ForgotPassword';
import AdminDashboard from './pages/admin/Dashboard';
import AdminDocuments from './pages/admin/Documents';
import AdminWidgets from './pages/admin/Widgets';
import AdminProfile from './pages/admin/Profile';
import AdminChat from './pages/admin/Chat';
import AdminBilling from './pages/admin/Billing';
import AdminSettings from './pages/admin/Settings';
import AdminCompleteSetup from './pages/admin/CompleteSetup';
import AdminUpgrade from './pages/admin/Upgrade';

import { superAdminAPI } from './services/api';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected route for superadmin
function SuperAdminProtectedRoute({ children }) {
  const token = localStorage.getItem('super_admin_token');
  return token ? children : <Navigate to="/superadmin/login" />;
}

// Protected route for tenant admin
function AdminProtectedRoute({ children, allowIncomplete = false, requireSubscription = true }) {
  const { isAuthenticated, loading, subscriptionStatus, isSubscriptionActive } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // New signups — redirect to Stripe checkout
  if (subscriptionStatus === 'incomplete' && !allowIncomplete) {
    return <Navigate to="/admin/complete-setup" replace />;
  }

  // Paywall — block canceled, past_due, trial_expired
  if (requireSubscription && !isSubscriptionActive && subscriptionStatus !== 'incomplete') {
    return <Navigate to="/admin/billing" state={{ paywall: true }} replace />;
  }

  return children;
}

// Tenant Admin Routes wrapped with providers
function AdminRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/admin" /> : <AdminLogin />} />
      <Route path="/signup" element={isAuthenticated ? <Navigate to="/admin" /> : <AdminSignup />} />
      <Route path="/forgot-password" element={isAuthenticated ? <Navigate to="/admin" /> : <AdminForgotPassword />} />
      <Route path="/complete-setup" element={
        <AdminProtectedRoute allowIncomplete={true} requireSubscription={false}>
          <AdminCompleteSetup />
        </AdminProtectedRoute>
      } />
      <Route path="/upgrade" element={
        <AdminProtectedRoute requireSubscription={false}>
          <AdminUpgrade />
        </AdminProtectedRoute>
      } />
      <Route path="/" element={
        <AdminProtectedRoute>
          <AdminLayout>
            <AdminDashboard />
          </AdminLayout>
        </AdminProtectedRoute>
      } />
      <Route path="/documents" element={
        <AdminProtectedRoute>
          <AdminLayout>
            <AdminDocuments />
          </AdminLayout>
        </AdminProtectedRoute>
      } />
      <Route path="/widgets" element={
        <AdminProtectedRoute>
          <AdminLayout>
            <AdminWidgets />
          </AdminLayout>
        </AdminProtectedRoute>
      } />
      <Route path="/profile" element={
        <AdminProtectedRoute requireSubscription={false}>
          <AdminLayout>
            <AdminProfile />
          </AdminLayout>
        </AdminProtectedRoute>
      } />
      <Route path="/chat" element={
        <AdminProtectedRoute>
          <AdminLayout>
            <AdminChat />
          </AdminLayout>
        </AdminProtectedRoute>
      } />
      <Route path="/billing" element={
        <AdminProtectedRoute requireSubscription={false}>
          <AdminLayout>
            <AdminBilling />
          </AdminLayout>
        </AdminProtectedRoute>
      } />
      <Route path="/settings" element={
        <AdminProtectedRoute requireSubscription={false}>
          <AdminLayout>
            <AdminSettings />
          </AdminLayout>
        </AdminProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  const [setupRequired, setSetupRequired] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Only check superadmin setup if we're on a superadmin route
    if (window.location.pathname.startsWith('/superadmin')) {
      checkSetupStatus();
    } else {
      setIsLoading(false);
    }
  }, []);

  const checkSetupStatus = async () => {
    try {
      const response = await superAdminAPI.checkSetupStatus();
      setSetupRequired(response.setup_required);
    } catch (error) {
      console.error('Error checking setup status:', error);
      setSetupRequired(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetupComplete = () => {
    window.location.href = '/superadmin/';
  };

  // Show loading screen while checking setup status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show setup wizard if setup is required (only for superadmin routes)
  if (setupRequired && window.location.pathname.startsWith('/superadmin')) {
    return (
      <QueryClientProvider client={queryClient}>
        <SetupWizard onComplete={handleSetupComplete} />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="App">
          <Routes>
            {/* Landing page at root */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />

            {/* Superadmin Routes */}
            <Route path="/superadmin/login" element={<Login />} />
            <Route path="/superadmin" element={
              <SuperAdminProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </SuperAdminProtectedRoute>
            } />
            <Route path="/superadmin/customers" element={
              <SuperAdminProtectedRoute>
                <Layout>
                  <Customers />
                </Layout>
              </SuperAdminProtectedRoute>
            } />
            <Route path="/superadmin/analytics" element={
              <SuperAdminProtectedRoute>
                <Layout>
                  <Analytics />
                </Layout>
              </SuperAdminProtectedRoute>
            } />
            <Route path="/superadmin/system" element={
              <SuperAdminProtectedRoute>
                <Layout>
                  <SystemHealth />
                </Layout>
              </SuperAdminProtectedRoute>
            } />
            <Route path="/superadmin/settings" element={
              <SuperAdminProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </SuperAdminProtectedRoute>
            } />
            <Route path="/superadmin/invite-links" element={
              <SuperAdminProtectedRoute>
                <Layout>
                  <InviteLinks />
                </Layout>
              </SuperAdminProtectedRoute>
            } />

            {/* Tenant Admin Routes - wrapped with Auth and Config providers */}
            <Route path="/admin/*" element={
              <ConfigProvider>
                <AuthProvider>
                  <UploadProvider>
                    <AdminRoutes />
                  </UploadProvider>
                </AuthProvider>
              </ConfigProvider>
            } />
          </Routes>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
